import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Col,
  Row,
} from 'antd';
import { gql, useQuery, useMutation } from '@common/hooks';
import moment from 'moment';
import {
  createMessage,
  mutationResponseMessage,
} from 'works/legacy/lib/message-helper';
import { UpdateOrganizationProfileMutation } from 'works/legacy/graphql/organization-profile';
import { US_DATE_FORMAT } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';

const ORGANIZATION_PROFILE_QUERY = gql`
  query OrganizationProfileQuery($id: ID!) {
    organizationProfile(id: $id) {
      id
      employeeId
      estimatedConversionDate
      contractHoursRemaining
      conversionDate
      estimatedArrivalDate
      arrivedAtDate
      worksProfile {
        id
        isInternational
      }
    }
  }
`;

function EditOrganizationProfileModal({
  organizationProfileId,
  visible,
  onClose,
}) {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const { error: organizationProfileError, data: organizationProfileData } =
    useQuery(ORGANIZATION_PROFILE_QUERY, {
      variables: {
        id: organizationProfileId,
      },
    });
  const orgProfile = organizationProfileData?.organizationProfile;

  const initialValues = {
    employeeId: orgProfile?.employeeId,
    contractHoursRemaining: orgProfile?.contractHoursRemaining,
    estimatedConversionDate: orgProfile?.estimatedConversionDate
      ? moment(orgProfile.estimatedConversionDate)
      : null,
    conversionDate: orgProfile?.conversionDate
      ? moment(orgProfile.conversionDate)
      : null,
    estimatedArrivalDate: orgProfile?.estimatedArrivalDate
      ? moment(orgProfile.estimatedArrivalDate)
      : null,
    arrivedAtDate: orgProfile?.arrivedAtDate
      ? moment(orgProfile.arrivedAtDate)
      : null,
  };

  const [editOrganizationProfile] = useMutation(
    UpdateOrganizationProfileMutation
  );

  useEffect(() => {
    if (!orgProfile) return;

    if (!visible) {
      form.setFieldsValue(initialValues);
    }
  });

  const handleMutationCompletion = function () {
    createMessage('success', 'Candidate details have been updated!');
    onClose();
  };

  const handleSubmit = (values) => {
    setSubmitLoading(true);
    editOrganizationProfile({
      variables: {
        organizationProfileId: organizationProfileId,
        input: {
          employeeId: values.employeeId,
          estimatedConversionDate: values.estimatedConversionDate,
          conversionDate: values.conversionDate,
          contractHoursRemaining: values.contractHoursRemaining,
          estimatedArrivalDate: values.estimatedArrivalDate,
          arrivedAtDate: values.arrivedAtDate,
        },
      },
    }).then((response) => {
      setSubmitLoading(false);
      const objectErrors = response.data.updateOrganizationProfile.errors;
      if (objectErrors) {
        objectErrors.forEach((error) => {
          if (error.attribute === 'employeeId') {
            addErrorsToForm(form, objectErrors);
          }
        });
      } else {
        mutationResponseMessage({
          gqlResponse: response,
          onSuccess: handleMutationCompletion,
          errorMessage: 'Failed to update candidate details. Please try again.',
        });
      }
    });
  };

  if (!visible) return null;

  if (organizationProfileError)
    createMessage('error', 'Error fetching OrganizationProfile');

  return (
    <>
      <Modal
        title="Edit Candidate Details"
        visible={visible}
        onCancel={() => onClose()}
        okText="Update Candidate Details"
        confirmLoading={true}
        className="EmployeeOrganizationProfileModal"
        footer={[
          <Button key="back" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            htmlType="submit"
            onClick={form.submit}
            loading={submitLoading}
          >
            Save
          </Button>,
        ]}
      >
        <Form
          onFinish={handleSubmit}
          initialValues={initialValues}
          form={form}
          layout="vertical"
        >
          <Row>
            <Col span={12}>
              <Form.Item
                label="Employee ID"
                name="employeeId"
                rules={[
                  {
                    pattern: /^.{1,40}$/,
                    message: 'Can be no more than 40 characters.',
                  },
                ]}
              >
                <Input disabled={submitLoading} placeholder="Type EID" />
              </Form.Item>
            </Col>
          </Row>

          {orgProfile?.worksProfile?.isInternational && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  label="Estimated Arrival"
                  name="estimatedArrivalDate"
                >
                  <DatePicker
                    placeholder="Select a Date"
                    format={US_DATE_FORMAT}
                    style={{ width: '100%' }}
                    disabled={submitLoading}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Confirmed Arrival" name="arrivedAtDate">
                  <DatePicker
                    placeholder="Select a Date"
                    format={US_DATE_FORMAT}
                    style={{ width: '100%' }}
                    disabled={submitLoading}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {orgProfile?.worksProfile?.isInternational && (
            <Row>
              <Col span={12}>
                <Form.Item
                  label="Hours Remaining in Contract"
                  name="contractHoursRemaining"
                >
                  <InputNumber
                    disabled={submitLoading}
                    min={0}
                    max={999}
                    style={{ width: '100%' }}
                    placeholder="Hours remaining"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={[16, 0]}>
            {orgProfile?.worksProfile?.isInternational && (
              <Col span={12}>
                <>
                  <Form.Item
                    label="Estimated Conversion Date"
                    name="estimatedConversionDate"
                  >
                    <DatePicker
                      placeholder="Select a Date"
                      format={US_DATE_FORMAT}
                      style={{ width: '100%' }}
                      disabled={submitLoading}
                    />
                  </Form.Item>
                </>
              </Col>
            )}
            <Col span={12}>
              <Form.Item label="Conversion Date" name="conversionDate">
                <DatePicker
                  placeholder="Select a Date"
                  format={US_DATE_FORMAT}
                  style={{ width: '100%' }}
                  disabled={submitLoading}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <style jsx>{`
        :global(.EmployeeOrganizationProfileModal .ant-modal-footer) {
          text-align: right;
        }
      `}</style>
    </>
  );
}

EditOrganizationProfileModal.propTypes = {
  organizationProfileId: PropTypes.string.isRequired,
  employeeId: PropTypes.string,
  conversionDate: PropTypes.string,
  estimatedConversionDate: PropTypes.string,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
};

export { EditOrganizationProfileModal };
